/**
 * Multi-currency foundation (2026-07) — the frontend's single source of truth
 * for the store's base currency and the supported-currency list.
 *
 * Seeded by SettingsContext from GET /api/app-settings, which only includes
 * `base_currency` + `currencies` while the backend's MULTICURRENCY_ENABLED
 * flag is on. Until seeded, every function returns the legacy value
 * (base 'gbp', symbol '£', the legacy five-option select list) — so with the
 * flag off the UI renders byte-identically to the pre-multicurrency build.
 *
 * Module-level (not a hook) so non-component code — sectionConfig builders,
 * column definitions, helpers — can read it too. React components can use
 * SettingsContext directly when they need re-render on change; in practice
 * settings load before any money renders (see Settings.jsx).
 */

import { useAllFx } from "../hooks/fx";

// Mirrors config/currencies.php on the backend. Used only until/unless the
// API supplies its own list.
const DEFAULT_CURRENCIES = [
  { id: "gbp", code: "GBP", symbol: "£", name: "British Pound", decimals: 2 },
  { id: "eur", code: "EUR", symbol: "€", name: "Euro", decimals: 2 },
  { id: "usd", code: "USD", symbol: "$", name: "US Dollar", decimals: 2 },
  { id: "hkd", code: "HKD", symbol: "HK$", name: "Hong Kong Dollar", decimals: 2 },
  { id: "chf", code: "CHF", symbol: "CHF ", name: "Swiss Franc", decimals: 2 },
  { id: "aed", code: "AED", symbol: "AED ", name: "UAE Dirham", decimals: 2 },
  { id: "usdt", code: "USDT", symbol: "$", name: "Tether", decimals: 2 },
];

// The pre-multicurrency inline option list used by QuoteForm / PaymentRow /
// CostFields — served verbatim while multi-currency is off so those selects
// don't change.
const LEGACY_OPTION_IDS = ["gbp", "eur", "usd", "hkd", "usdt"];

const state = {
  base: "gbp",
  currencies: DEFAULT_CURRENCIES,
  multicurrency: false,
};

/** Called by SettingsContext once /api/app-settings resolves. */
export const seedCurrency = (baseCurrency, currencies) => {
  if (!baseCurrency) {
    // Flag off on the backend — stay in legacy mode.
    return;
  }
  state.base = String(baseCurrency).toLowerCase();
  state.multicurrency = true;
  if (Array.isArray(currencies) && currencies.length) {
    state.currencies = currencies;
  }
};

export const isMulticurrency = () => state.multicurrency;

/**
 * FX aliases — currencies pegged 1:1 to another, which therefore have no rate
 * of their own. USDT is a USD-pegged stablecoin: 200 USDT IS 200 USD.
 *
 * The backend agrees but only halfway: CurrencyConverterService::getRate()
 * rewrites USDT→USD on the FROM side only, getRates() (`/api/fx/all/{to}`)
 * omits USDT from its table entirely, and Frankfurter — the upstream provider —
 * doesn't quote crypto at all. So an un-aliased lookup either finds no rate
 * (the base bracket silently disappears) or falls back to the caller's `1`,
 * which quietly claims parity between USDT and whatever the other currency is.
 * Alias it here, once, so every rate lookup in the app resolves USDT via USD.
 */
export const FX_ALIASES = { usdt: "usd" };

/** Lower-cased currency id with 1:1 pegs resolved to their peg (usdt → usd). */
export const fxCode = (code) => {
  const c = String(code || "").toLowerCase();
  return FX_ALIASES[c] ?? c;
};

/** Store base currency, lower-case ('gbp' until seeded). */
export const getBaseCurrency = () => state.base;

export const currencyMeta = (code) =>
  state.currencies.find((c) => c.id === String(code || "").toLowerCase()) || null;

/** Display symbol; unknown/absent codes keep the legacy '£'. */
export const symbolFor = (code) => currencyMeta(code)?.symbol ?? "£";

export const baseSymbol = () => symbolFor(state.base);

/**
 * Options for currency <select>s ({id, name} shape used across the app).
 * Legacy five while multi-currency is off; the full supported list when on.
 */
export const currencyOptions = () => {
  const ids = state.multicurrency
    ? state.currencies.map((c) => c.id)
    : LEGACY_OPTION_IDS;
  return ids.map((id) => ({ id, name: id.toUpperCase() }));
};

/**
 * Base-eligible currency options ({id, name}) — excludes USDT and any other
 * non-base currency. Used where the value must be a fiat base currency (store
 * base, request budgets — which format via ISO currency rules). Falls back to
 * the legacy GBP-only option until multi-currency is seeded.
 */
export const baseCurrencyOptions = () => {
  if (!state.multicurrency) {
    return [{ id: "gbp", name: "GBP" }];
  }
  return state.currencies
    .filter((c) => c.base_eligible)
    .map((c) => ({ id: c.id, name: c.code || c.id.toUpperCase() }));
};

/**
 * Standard money renderer for pence/minor units:
 *   formatMoney(123456) → "£1,234.56" (base currency)
 *   formatMoney(123456, "eur") → "€1,234.56"
 *   formatMoney(123400, { decimals: 0 }) → "£1,234"
 * Locale is fixed to en-GB (grouping style), matching the app's existing
 * output; the SYMBOL follows the currency.
 *
 * Display rounding: unless the caller passes `decimals`, amounts render with
 * exactly TWO decimal places (rounded, zero-padded) — display-only, stored
 * values are never mutated.
 */
const renderAmount = (value, currency, decimals) => {
  // Sign goes BEFORE the symbol (legacy Intl currency style: "-£5", not "£-5").
  const sign = value < 0 ? "-" : "";
  const formatted = Math.abs(value).toLocaleString("en-GB", {
    minimumFractionDigits: decimals ?? 2,
    maximumFractionDigits: decimals ?? 2,
  });

  return `${sign}${symbolFor(currency)}${formatted}`;
};

export const formatMoney = (pence, opts = {}) => {
  const { currency = state.base, decimals } =
    typeof opts === "string" ? { currency: opts } : opts;

  return renderAmount((Number(pence) || 0) / 100, currency, decimals);
};

/**
 * Major units (number or string, e.g. "2.50") → integer minor units. L11.
 * Rounds rather than truncates: 19.99 * 100 is 1998.9999999999998 in floats,
 * and parseInt("2.50") is 2.
 */
export const toPence = (major) => Math.round((parseFloat(major) || 0) * 100);

/** As formatMoney but for amounts already in major units (not pence). */
export const formatMoneyMajor = (amount, opts = {}) => {
  const { currency = state.base, decimals } =
    typeof opts === "string" ? { currency: opts } : opts;

  return renderAmount(Number(amount) || 0, currency, decimals);
};

/**
 * Q9 bracket rule — every money field whose currency differs from the store
 * base ALSO shows the base-converted value in brackets:
 *   formatMoneyWithBase(100000, "usd", 7.81) → "$1,000 (HK$7,810)" (HKD base)
 * Same currency as base → plain formatMoney, no brackets. `rateToBase`
 * converts ONE unit of `currency` into the base; when it's absent/unknown the
 * bracket is omitted rather than shown unconverted (never display a wrong
 * figure). Display-only — stored values are never mutated.
 */
export const formatMoneyWithBase = (pence, currency, rateToBase, opts = {}) => {
  const ccy = String(currency || state.base).toLowerCase();
  const main = formatMoney(pence, { ...opts, currency: ccy });
  // Peg-aware: a USDT amount in a USD-base store needs no bracket — it would
  // only restate the same number ("$200 ($200)").
  if (fxCode(ccy) === fxCode(state.base)) return main;
  const rate = Number(rateToBase);
  if (!rate || !isFinite(rate)) return main;
  const inBase = (Number(pence) || 0) * rate;
  return `${main} (${formatMoney(inBase, { ...opts, currency: state.base })})`;
};

/**
 * Hook-bound variant of formatMoneyWithBase: binds the FX table (any currency
 * → store base, via useAllFx) so callers just do fm(pence, currency, opts?).
 * With multi-currency off / rates unseeded, useAllFx returns {} and every
 * render collapses to plain formatMoney — byte-identical legacy output.
 */
export const useFormatMoneyWithBase = () => {
  const base = state.base;
  const { rates } = useAllFx(base);
  return (pence, currency, opts = {}) => {
    const ccy = String(currency || base).toLowerCase();
    // crossRate (not a raw rates[] lookup) so pegged currencies resolve via
    // their peg — rates has no USDT row, so USDT lost its bracket entirely.
    return formatMoneyWithBase(pence, ccy, crossRate(rates, ccy, base), opts);
  };
};

/**
 * Rate converting ONE unit of `from` into `to`, read out of a single
 * useAllFx(base) table (rates[CODE] = CODE → base). Cross-rates go via the
 * base — (from→base) ÷ (to→base) — so one already-fetched table serves every
 * pair. That matters because the rules of hooks forbid a useFx() per row, and
 * money rows are rendered in loops with per-row currencies.
 *
 * Identical (or mutually pegged) currencies always return exactly 1 and never
 * touch the table, so a single-currency deal is immune to FX availability.
 * Returns null when either leg is unknown: callers must then OMIT the figure
 * rather than render an unconverted one — the same rule formatMoneyWithBase
 * applies to its bracket. Never display a wrong number.
 */
export const crossRate = (rates, from, to) => {
  const f = fxCode(from) || fxCode(state.base);
  const t = fxCode(to) || fxCode(state.base);
  if (f === t) return 1;
  const leg = (code) => {
    // The table's own target may be omitted from it; it is 1 by definition.
    if (code === fxCode(state.base)) return 1;
    const r = Number(rates?.[code.toUpperCase()]);
    return r && isFinite(r) ? r : null;
  };
  const fromLeg = leg(f);
  const toLeg = leg(t);
  if (!fromLeg || !toLeg) return null;
  return fromLeg / toLeg;
};

/** Hook-bound crossRate: (from, to = base) => rate | null. */
export const useCrossRate = () => {
  const base = state.base;
  const { rates } = useAllFx(base);
  return (from, to = base) => crossRate(rates, from, to);
};

/**
 * ORIGINAL-money resolver — the ONE place that decides which stored figure a
 * money field should render. Use it at every cost/price display site.
 *
 * Records holding money also hold a GBP pivot (`gbp_*`, written by the backend
 * as amount × rate(original currency → GBP); see ListingService::
 * updateGbpAmounts). Rendering the pivot round-trips the user's own number
 * through two conversions — a typed 1000 USD becomes gbp_buying_price 747 and
 * comes back as 993 USD — so rounding and rate drift corrupt a value somebody
 * typed by hand. Prefer the ORIGINAL amount together with its own currency
 * column, and fall back to the pivot ONLY for legacy rows that have no
 * original currency stored.
 *
 * The returned pair is self-consistent: `amount` is always denominated in the
 * returned `currency`, so callers can never mislabel it. `isOriginal` is false
 * exactly when the figure came from (or had to be assumed to be) the pivot.
 *
 * @param {object} record            listing / cost row
 * @param {string|string[]} fields   original amount column(s) — summed
 * @param {string} currencyField     column holding those amounts' currency
 * @param {string[]} [gbpFields]     pivot columns (default `gbp_<field>`)
 * @returns {{amount:number, currency:string, isOriginal:boolean}|null}
 */
export const resolveOriginalMoney = (record, fields, currencyField, gbpFields) => {
  if (!record) return null;
  const names = Array.isArray(fields) ? fields : [fields];
  const present = (keys) =>
    keys.some((k) => record[k] != null && record[k] !== "");
  const sum = (keys) => keys.reduce((acc, k) => acc + (Number(record[k]) || 0), 0);
  const currency = fxCode(record[currencyField]);

  if (currency && present(names)) {
    return { amount: sum(names), currency, isOriginal: true };
  }

  const pivots = gbpFields ?? names.map((k) => `gbp_${k}`);
  if (present(pivots)) {
    // INVARIANT: this row has no original currency, so the GBP pivot is the
    // only figure that exists — converting it is the best available answer.
    return { amount: sum(pivots), currency: "gbp", isOriginal: false };
  }
  if (present(names)) {
    // Originals with no currency column and no pivot: the backend's own
    // getGbpAmount() treats a null currency as GBP, so mirror that.
    return { amount: sum(names), currency: "gbp", isOriginal: false };
  }
  return null;
};

/**
 * The currency an ORDER ITEM's `price` is denominated in.
 *
 * `order_items.price` is a SNAPSHOT of `listings.price`, re-synced from it
 * whenever the order total is recomputed, and `order_items.currency` is written
 * on the same paths. So there are two records that could describe the amount,
 * and which one is authoritative depends on whether they still agree:
 *
 *   · prices EQUAL   → the LIVE listing's currency. The item is a copy of that
 *     listing's current price, and the listing is the maintained record.
 *     order_items.currency was defaulted to 'GBP' on every row created before
 *     `listings.currency` existed (migration 2026_07_21_150001), so it can be
 *     stale in a way the price is not — production order 87362 holds a €26,850
 *     item stamped 'gbp'.
 *   · prices DIFFER  → the ITEM's own currency. The listing has been re-priced
 *     since the order was placed, so its currency no longer describes this
 *     snapshot; the item's own label is the only thing that does.
 *
 * Either way the answer comes off the record that actually describes the amount
 * — never the deal/order currency, which belongs to a different record. A row
 * with no currency at all is legacy GBP.
 *
 * This is the same equality test `orders:resync-currency` uses as its safety
 * gate, deliberately: one rule for reading an order item, one for repairing it.
 */
export const orderItemCurrency = (item, listing = item?.listing) => {
  const own = fxCode(item?.currency);
  const live = fxCode(listing?.currency);
  const agree =
    item?.price != null &&
    listing?.price != null &&
    Number(item.price) === Number(listing.price);

  return (agree ? live || own : own || live) || "gbp";
};

/**
 * GBP-pivot → store base, for figures whose original currency genuinely
 * cannot be recovered: cross-record aggregates and server-side `*_gbp` totals,
 * where many rows (each possibly entered in a different currency) have already
 * been summed into one GBP number. Prefer resolveOriginalMoney wherever the
 * record still carries its own amount + currency.
 *
 * When GBP→base is unknown, `rate` is null and `format` labels the figure GBP
 * instead of stamping the base symbol onto an unconverted number — which is
 * precisely the bug this exists to prevent.
 */
export const useGbpToBase = () => {
  const base = state.base;
  const rate = useCrossRate()("gbp", base);
  const unavailable = rate == null;
  const toBase = (amount) => (unavailable ? null : (Number(amount) || 0) * rate);

  return {
    rate,
    toBase,
    /** Currency the values are actually denominated in once formatted. */
    currency: unavailable ? "gbp" : base,
    symbol: symbolFor(unavailable ? "gbp" : base),
    format: (amount, opts = {}) =>
      unavailable
        ? formatMoney(amount, { ...opts, currency: "gbp" })
        : formatMoney(toBase(amount), { ...opts, currency: base }),
  };
};
